import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../api/useApi";
import { availableOrganizations } from "../../api/organizationQueries";
import {
  updateOrganizationEnabledFlag,
  setOrganizationToConsentState,
} from "../../api/organizationMutations";
import DataTable from "../../components/Table/DataTable";
import textFilterToWhereClause from "../../components/Table/helpers/textFilterToWhere";
import { Link } from "react-router-dom";
import Button from "../../components/Button";
import StyledLink from "../../components/StyledLink";
import { MdVerifiedUser, MdArchive, MdSync } from "react-icons/md";
import { organizationAdd, organization } from "../../common/paths";
import Modal from "../../components/Modal";
import Spinner from "../../components/Loaders/Spinner";
import ErrorMessages from "../../components/Notifications/ErrorMessages";
import TableButton from "../../components/Button/TableButton";

const Organization = () => {
  const [{ loading, errors, data }, doFetch] = useApi();
  const [
    { loading: removalLoading, errors: removalErrors, data: removalData },
    remove,
  ] = useApi();
  const [{ loading: syncLoading, errors: syncErrors, data: syncData }, sync] =
    useApi();

  const [showConfirm, setShowConfirm] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // Bumped to force <DataTable> to refetch the current page after a mutation.
  const [refreshKey, setRefreshKey] = useState(0);

  const organizations = data?.availableOrganizations?.edges ?? [];
  const totalCount = data?.availableOrganizations?.totalCount;
  const pageInfo = data?.availableOrganizations?.pageInfo;

  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.displayName,
      Cell: ({ row: { original } }) => {
        const isCoreOrAdmin =
          original?.node?.role === "ADMINISTRATOR" ||
          original?.node?.role === "CORE";

        return original?.node?.enabled ? (
          <StyledLink to={organization(original?.node?.id)}>
            {isCoreOrAdmin && (
              <>
                <MdVerifiedUser title={original?.node?.role} />{" "}
              </>
            )}
            {original?.node?.displayName}
          </StyledLink>
        ) : (
          original?.node?.displayName
        );
      },
    },
    {
      Header: "Id",
      id: "id",
      // Int field — not a string-filter target.
      disableColumnMenu: true,
      accessor: (d) => d?.node?.id,
    },
    {
      Header: "Status",
      id: "status",
      // Derived boolean (Pending/Active) — not a string-filter target.
      disableColumnMenu: true,
      accessor: (d) => d?.node?.isPending,
      Cell: ({ row: { original } }) => {
        return original?.node?.isPending ? "Pending" : "Active";
      },
    },
    {
      Header: "Enabled",
      id: "enabled",
      // Controlled by the Show/Hide Disabled toggle (base where).
      disableColumnMenu: true,
      accessor: (d) => d?.node?.enabled,
      Cell: ({ row: { original } }) => {
        return original?.node?.enabled.toString();
      },
    },
    {
      Header: "",
      id: "actions",
      maxWidth: 150,
      disableResizing: true,
      Cell: ({ row: { original } }) => {
        const enabled = original?.node?.enabled;
        const pending = original?.node?.isPending;

        return (
          <>
            {pending && enabled ? (
              <TableButton
                title="Force Sync"
                type="button"
                list={"true"}
                onClick={() =>
                  sync({
                    query: setOrganizationToConsentState,
                    variables: { id: original?.node?.id },
                  })
                }
              >
                {syncLoading ? <Spinner /> : <MdSync />}
              </TableButton>
            ) : null}

            <TableButton
              title="Archive Toggle"
              type="button"
              danger={enabled}
              onClick={() =>
                setOrgToDelete({ id: original?.node?.id, enabled: enabled })
              }
            >
              <MdArchive />
            </TableButton>
          </>
        );
      },
    },
  ];

  // Server-side paged fetch. `showArchived` selects enabled vs disabled orgs
  // (previously filtered client-side); `refreshKey` forces a refetch after a
  // mutation.
  const fetchData = useCallback(
    ({ pageSize, cursor, filters }) => {
      // Base where (Show/Hide Disabled toggle) + the Name column filter mapped
      // to displayName (AC-15498).
      const where = { enabled: { eq: !showArchived } };
      const nameFilter = filters?.find((f) => f.id === "name");
      if (nameFilter?.value) {
        const clause = textFilterToWhereClause(nameFilter.value);
        if (clause) where.displayName = clause;
      }

      doFetch({
        query: availableOrganizations,
        variables: {
          first: pageSize,
          after: cursor,
          where,
          order: { displayName: "ASC" },
        },
      });
    },
    [doFetch, showArchived, refreshKey]
  );

  const toggleEnabled = ({ id, enabled }) => {
    remove({
      query: updateOrganizationEnabledFlag,
      variables: { id: id, enabledState: !enabled },
    });
  };

  //handle remove rule update
  useEffect(() => {
    if (removalData) {
      setRefreshKey((k) => k + 1);
      setShowConfirm(false);
      setOrgToDelete(null);
      setShowArchived(false);
    }
  }, [removalData]);

  useEffect(() => {
    if (orgToDelete) {
      setShowConfirm(true);
    } else {
      setShowConfirm(false);
    }
  }, [orgToDelete]);

  useEffect(() => {
    if (syncData) {
      setRefreshKey((k) => k + 1);
      setShowConfirm(false);
      setShowArchived(false);
    }
  }, [syncData]);

  return (
    <>
      {showConfirm ? (
        <Modal
          title={`Confirm Organization ${
            orgToDelete?.enabled ? "Archival" : "Reactivation"
          }`}
          hide={() => setOrgToDelete(null)}
        >
          <p>
            Are you sure you wish to{" "}
            {orgToDelete?.enabled ? "archive" : "reactivate"} this Organization?
          </p>
          {orgToDelete?.enabled ? (
            <p>
              This action will remove all current Workgroups and Permissions.
            </p>
          ) : null}

          <div>
            <Button
              type="button"
              list="true"
              disabled={removalLoading}
              danger
              onClick={() => toggleEnabled(orgToDelete)}
            >
              {removalLoading ? <Spinner /> : "Yes"}
            </Button>
            <Button
              type="button"
              disabled={removalLoading}
              onClick={() => {
                setOrgToDelete(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      ) : null}

      <Link to={organizationAdd}>
        <Button list={"true"} type="button">
          Add Organization
        </Button>
      </Link>

      <Button
        type="button"
        onClick={() => setShowArchived((prevState) => !prevState)}
      >
        {showArchived ? "Hide " : "Show "} Archived
      </Button>

      {syncErrors ? <ErrorMessages errors={syncErrors} /> : null}
      {removalErrors ? <ErrorMessages errors={removalErrors} /> : null}
      {errors ? <ErrorMessages errors={errors} /> : null}
      <DataTable
        key={showArchived ? "archived" : "active"}
        fetchData={fetchData}
        totalCount={totalCount}
        pageInfo={pageInfo}
        loading={loading}
        data={organizations}
        columns={columnsData}
        columnMenu
        defaultPageSize={50}
        // Server query is hardcoded to order by displayName ASC; built-in header
        // sort would be misleading (it can't reorder across pages). Server-side
        // sort wiring is deferred to the sort/filter UX work in AC-15377.
        disableSortBy
      />
    </>
  );
};

export default Organization;
